import { useCallback, useEffect, useRef, useState } from 'react';

export type SerializedSaveQueueStatus = 'idle' | 'dirty' | 'saving' | 'saved' | 'failed' | 'conflicted';

export interface SerializedSaveContext {
  sequence: number;
  isLatest: () => boolean;
}

export interface UseSerializedSaveQueueOptions<TPayload> {
  save: (payload: TPayload, context: SerializedSaveContext) => Promise<void>;
  areEqual?: (left: TPayload, right: TPayload) => boolean;
  isConflictError?: (error: unknown) => boolean;
  savedStatusDurationMs?: number | null;
}

export interface SerializedSaveQueue<TPayload> {
  status: SerializedSaveQueueStatus;
  enqueueSave: (payload: TPayload) => Promise<void>;
  markDirty: () => void;
}

interface QueueWaiter {
  resolve: () => void;
  reject: (error: unknown) => void;
}

interface QueueSlot<TPayload> {
  sequence: number;
  payload: TPayload;
  waiters: QueueWaiter[];
}

const defaultAreEqual = <TPayload>(left: TPayload, right: TPayload): boolean => Object.is(left, right);

const resolveWaiters = <TPayload>(slot: QueueSlot<TPayload>) => {
  slot.waiters.forEach((waiter) => waiter.resolve());
  slot.waiters = [];
};

const rejectWaiters = <TPayload>(slot: QueueSlot<TPayload>, error: unknown) => {
  slot.waiters.forEach((waiter) => waiter.reject(error));
  slot.waiters = [];
};

const addWaiter = <TPayload>(slot: QueueSlot<TPayload>): Promise<void> =>
  new Promise((resolve, reject) => {
    slot.waiters.push({ resolve, reject });
  });

export function useSerializedSaveQueue<TPayload>(options: UseSerializedSaveQueueOptions<TPayload>): SerializedSaveQueue<TPayload> {
  const [status, setStatusState] = useState<SerializedSaveQueueStatus>('idle');
  const optionsRef = useRef(options);
  const statusRef = useRef<SerializedSaveQueueStatus>('idle');
  const inFlightRef = useRef<QueueSlot<TPayload> | null>(null);
  const pendingRef = useRef<QueueSlot<TPayload> | null>(null);
  const latestSequenceRef = useRef(0);
  const latestEnqueuedSequenceRef = useRef(0);
  const savedStatusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  useEffect(
    () => () => {
      isMountedRef.current = false;

      if (savedStatusTimerRef.current) {
        clearTimeout(savedStatusTimerRef.current);
      }
    },
    []
  );

  const clearSavedStatusTimer = useCallback(() => {
    if (savedStatusTimerRef.current) {
      clearTimeout(savedStatusTimerRef.current);
      savedStatusTimerRef.current = null;
    }
  }, []);

  const setQueueStatus = useCallback(
    (nextStatus: SerializedSaveQueueStatus) => {
      clearSavedStatusTimer();
      statusRef.current = nextStatus;

      if (isMountedRef.current) {
        setStatusState(nextStatus);
      }

      if (nextStatus !== 'saved') {
        return;
      }

      const savedStatusDurationMs =
        optionsRef.current.savedStatusDurationMs === undefined ? 2000 : optionsRef.current.savedStatusDurationMs;

      if (savedStatusDurationMs === null) {
        return;
      }

      savedStatusTimerRef.current = setTimeout(() => {
        if (statusRef.current !== 'saved') {
          return;
        }

        statusRef.current = 'idle';

        if (isMountedRef.current) {
          setStatusState('idle');
        }
      }, savedStatusDurationMs);
    },
    [clearSavedStatusTimer]
  );

  const reserveSequence = () => {
    const sequence =
      latestSequenceRef.current > latestEnqueuedSequenceRef.current ? latestSequenceRef.current : latestSequenceRef.current + 1;

    latestSequenceRef.current = sequence;
    latestEnqueuedSequenceRef.current = sequence;

    return sequence;
  };

  const runSlot = useCallback(
    (slot: QueueSlot<TPayload>) => {
      inFlightRef.current = slot;
      setQueueStatus('saving');

      void (async () => {
        try {
          await optionsRef.current.save(slot.payload, {
            sequence: slot.sequence,
            isLatest: () => slot.sequence === latestSequenceRef.current
          });

          resolveWaiters(slot);
          inFlightRef.current = null;

          const pendingSlot = pendingRef.current;
          if (pendingSlot) {
            pendingRef.current = null;
            runSlot(pendingSlot);
            return;
          }

          setQueueStatus(slot.sequence === latestSequenceRef.current ? 'saved' : 'dirty');
        } catch (error) {
          rejectWaiters(slot, error);
          inFlightRef.current = null;

          const pendingSlot = pendingRef.current;
          if (pendingSlot) {
            pendingRef.current = null;
            rejectWaiters(pendingSlot, error);
          }

          setQueueStatus(optionsRef.current.isConflictError?.(error) ? 'conflicted' : 'failed');
        }
      })();
    },
    [setQueueStatus]
  );

  const markDirty = useCallback(() => {
    latestSequenceRef.current += 1;
    clearSavedStatusTimer();

    if (inFlightRef.current) {
      setQueueStatus('saving');
      return;
    }

    setQueueStatus('dirty');
  }, [clearSavedStatusTimer, setQueueStatus]);

  const enqueueSave = useCallback(
    (payload: TPayload): Promise<void> => {
      const areEqual = optionsRef.current.areEqual ?? defaultAreEqual;
      const inFlightSlot = inFlightRef.current;
      const pendingSlot = pendingRef.current;

      if (pendingSlot && areEqual(payload, pendingSlot.payload)) {
        if (latestSequenceRef.current > pendingSlot.sequence) {
          pendingSlot.sequence = latestSequenceRef.current;
          latestEnqueuedSequenceRef.current = latestSequenceRef.current;
        }

        return addWaiter(pendingSlot);
      }

      if (inFlightSlot && !pendingSlot && inFlightSlot.sequence === latestSequenceRef.current && areEqual(payload, inFlightSlot.payload)) {
        return addWaiter(inFlightSlot);
      }

      const savePromise = new Promise<void>((resolve, reject) => {
        const waiter = { resolve, reject };
        const sequence = reserveSequence();

        if (inFlightRef.current) {
          if (pendingRef.current) {
            pendingRef.current.sequence = sequence;
            pendingRef.current.payload = payload;
            pendingRef.current.waiters.push(waiter);
            return;
          }

          pendingRef.current = {
            sequence,
            payload,
            waiters: [waiter]
          };
          return;
        }

        runSlot({
          sequence,
          payload,
          waiters: [waiter]
        });
      });

      return savePromise;
    },
    [runSlot]
  );

  return {
    status,
    enqueueSave,
    markDirty
  };
}
